import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import Loader from '@/components/common/Loader';
import { inr } from '@/lib/format';

export default function Bundles() {
  const [bundles, setBundles] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get('/bundles').then((r) => setBundles(r.data)).catch(() => setBundles([]));
  }, []);

  if (bundles === null) return <Loader />;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[hsl(var(--gaze-ivory))]">
        <div className="container-gaze py-12 sm:py-16">
          <p className="eyebrow mb-2">Complete Room Sets · Curated Bundles</p>
          <h1 className="font-display text-4xl sm:text-5xl">Add the entire look in one tap</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Editor-curated bundles designed to work together. Beautifully priced, beautifully complete.</p>
        </div>
      </section>

      <section className="container-gaze py-10">
        {bundles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center">
            <Sparkles className="mx-auto mb-3 text-muted-foreground" size={26} />
            <p className="text-muted-foreground">New bundles arriving soon.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="bundles-grid">
            {bundles.map((b) => (
              <Link key={b.id} to={`/bundle/${b.slug}`} className="group overflow-hidden rounded-3xl border border-border bg-white transition hover:shadow-xl">
                <div className="relative aspect-[4/3] overflow-hidden">
                  {b.hero_image && <img src={b.hero_image} alt={b.name} className="h-full w-full object-cover transition group-hover:scale-105" />}
                  {b.is_featured && <span className="absolute left-3 top-3 rounded-full bg-[hsl(var(--gaze-gold))] px-2 py-0.5 text-[10px] font-semibold text-black">Editor&apos;s Pick</span>}
                  {!b.in_stock && <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white">Sold out</span>}
                </div>
                <div className="p-5">
                  <h2 className="font-display text-xl leading-tight">{b.name}</h2>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{b.short_description || b.description}</p>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-lg font-bold">{inr(b.bundle_price)}</span>
                    {b.discount > 0 && <span className="text-xs text-muted-foreground line-through">{inr(b.subtotal)}</span>}
                    {b.discount > 0 && <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--primary))]">Save {inr(b.discount)}</span>}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{b.product_count} pieces</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-[hsl(var(--primary))]"><ShoppingBag size={13} /> Shop the set</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
